
<template>
  <v-app id="vueapp">
    <header :class="connectionError ? 'header-error' : 'header'">
        <v-btn v-if="startPage === this.$route.name" text class="show-on-focus find-booking-btn" tabindex="0" ref="firstFocus" @click="focusFirstStep" id="skiplink">{{messages.tabFirstStep}}
        </v-btn>
      <router-link @click.native="resetFindBooking" target="_self" to="/search" v-if="configuration && configuration.showFind && !connectionError" tabindex="-1">
        <v-btn text :ripple="false" class="find-booking-btn" id="headerFindButton">
          <v-icon>search</v-icon>
          <div class="find-booking-text">{{messages.findAppointmentButton}}</div>
        </v-btn>
      </router-link>
      <div id="logo-header" class="brand" title="logo" role="presentation"></div>
      <!-- Load our language drop down menu component that will be displayed in the header in all views
      Pass needed data from our main Vue App to this component-->
      <language-dropdown
        v-if="selectedLanguage && languages.length > 1 && !connectionError"
        :selected-language="selectedLanguage"
        :languages="languages"
        @change-language="changeLanguage"
        id="language_dropdown"
      ></language-dropdown>
    </header>
    <v-container class="main-container" id="main-container" v-if="!connectionError">
      <router-view
        v-if="mounted"
        :start-page="startPage"
        :url-params="urlParams"
        :query-params="queryParams"
        :reload-key="reloadKey"
        @increase-reload-key="increaseReloadKey"
        @change-language="changeLanguage"
      />
    </v-container>
    <v-container class="main-container-error" v-else>
      <v-row>
        <v-col cols="12">
          {{connectionErrorMessage}}
        </v-col>
      </v-row>
      <v-row v-if="showReloadButton">
        <v-col cols="12">
          <v-btn @click='reload'>{{reloadButtonText}}</v-btn>
        </v-col>
      </v-row>
    </v-container>
    <custom-footer class="footer"
      :imageUrl="imageUrl">
    </custom-footer>
    <v-snackbar
      v-model="showError"
      top
      multi-line
      :timeout="errorDisplayTimeout"
      :color="errorColor"
      role="alert"
    >
      {{ errorMessage }}
      <v-btn
        primary-color
        text
        @click="showError = false"
        v-if="errorMessageCloseButtonVisible"
      >
        <v-icon color="white">cancel</v-icon>
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import LanguageDropdown from './components/LanguageDropdown'
import CustomFooter from './components/CustomFooter'
import RestService from './services/restService'
import DataService from './services/dataService'
import FormatService from './services/formatService'
import { eventHub } from './utils/eventhub'
import Package from '../package.json'
import { mapActions, mapState, mapMutations } from 'vuex'
import { setTimeout } from 'timers'
// use version number in package.json

export default {
  name: 'App',
  components: {
    LanguageDropdown,
    CustomFooter
  },
  mixins: [RestService, DataService, FormatService],
  data: () => ({
    showError: false,
    errorCode: null,
    errorMessage: '',
    errorDisplayTimeout: 6000,
    errorColor: '',
    errorMessageCloseButtonVisible: true,
    languages: [],
    version: null,
    clientVersion: null,
    urlParams: {},
    startPage: 'booking',
    reloadKey: 0,
    sessionLanguage: null,
    queryParams: {},
    imageUrl: '',
    mounted: false,
    connectionError: false,
    connectionErrorMessage: 'The appointment booking is currently not available',
    reloadButtonText: 'Reload',
    showReloadButton: true
  }),
  computed: {
    ...mapState([
      'configuration',
      'messages',
      'selectedLanguage',
      'funcDebug'
    ])
  },
  methods: {
    ...mapActions([
      'setConfiguration',
      'setMessages',
      'setDefaultCountryPrefix',
      'setSelectedLanguage',
      'setSecurityToken'
    ]),
    ...mapMutations([
      'setMessagesAgreementHtml1',
      'setMessagesAgreementHtml2',
      'setServiceTemplates'
    ]),
    /* executeTest () {
      eventHub.$emit('show-error', 'I have a lot of things to say men but this is another story!')
    }, */
    reload () {
      location.reload()
    },
    showMessage (textData, mode) {
      if (this.funcDebug) console.log('App:method:showMessage')
      if (this.connectionError) {
        // Do not show any other message than error page
        return
      }
      // first remove the old Message
      this.showError = false
      // wait a bit for the animation then add the message
      setTimeout(() => {
        switch (mode) {
          case 'info':
            this.errorDisplayTimeout = 6000
            this.errorColor = 'message-info-style'
            this.errorMessageCloseButtonVisible = false
            break
          case 'warning':
            this.errorDisplayTimeout = 6000
            this.errorColor = 'message-warning-style'
            this.errorMessageCloseButtonVisible = false
            break
          case 'error':
            this.errorDisplayTimeout = 10000
            this.errorColor = 'message-error-style'
            this.errorMessageCloseButtonVisible = false
            break
        }
        this.errorMessage = textData
        this.showError = true
      }, 250)
    },
    hideMessage () {
      if (this.funcDebug) console.log('App:method:hideMessage')
      this.showError = false
      this.$refs.firstFocus.$el.focus()
    },
    /*
    Function that will be triggered when:
    - A user chooses a language in the dropdown menu
    - A user clicks on the appointment link, then the language will be fetched in languageCode in the Appointment object
    The function will then fetch right uiMessages file
    */
    changeLanguage (language) {
      if (this.funcDebug) console.log('App:method:changeLanguage')
      var _this = this
      var thisSelectedLanguage
      if ((language && !language.lang && language !== this.selectedLanguage) || (language.lang && language !== this.selectedLanguage)) {
        if (language && language.lang) {
          thisSelectedLanguage = language
        } else {
        /*
        Since only language code is stored in the Appointment info, we need to loop through our languages array to fetch right language object
        */
          for (var i = 0; i < this.languages.length; i++) {
            var lang = this.languages[i]
            if (lang.lang === language) {
              thisSelectedLanguage = lang
              break
            }
          }
        }
        var composedLanguageCode = thisSelectedLanguage.lang + '_' + thisSelectedLanguage.countryCodeCountry
        composedLanguageCode = composedLanguageCode.toLowerCase()
        this.getMessages(composedLanguageCode).then(response => {
          _this.setMessages(response.data)
          _this.checkRTL()
          _this.replaceAgreementLinks()
          _this.setSelectedLanguage(thisSelectedLanguage)
          _this.setTitle()
          _this.sessionLanguage = composedLanguageCode
          // save session language
          localStorage.lang = _this.sessionLanguage
        })
        document.documentElement.setAttribute('lang', thisSelectedLanguage.lang)
      }
    },
    // Will be used when we can recieve entire languages array from the backend, then we won't have to hardcode it in the client
    initLanguage (configLanguages) {
      if (this.funcDebug) console.log('App:method:initLanguage')
      var _this = this
      if (this.funcDebug) console.log('Session language at beginning of init: ' + this.sessionLanguage)
      // have to check if sessionlanguage is in, then setup
      var sessionLanguageIsInConfig = false
      if (this.sessionLanguage) {
        // check if in
        for (var i = 0; i < configLanguages.length; i++) {
          let composedLanguageCode = configLanguages[i].lang + '_' + configLanguages[i].countryCodeCountry
          if (this.sessionLanguage === composedLanguageCode.toLowerCase()) {
            sessionLanguageIsInConfig = true
            break
          }
        }
      }
      for (let i = 0; i < configLanguages.length; i++) {
        var language = configLanguages[i]
        var composedLanguageCode = language.lang + '_' + language.countryCodeCountry
        composedLanguageCode = composedLanguageCode.toLowerCase()
        if (language.countryCode) {
          if (!language.countryCodeCountry) { language.countryCodeCountry = language.countryCode }
          if (!language.countryCodeLang) { language.countryCodeLang = language.countryCode }
        }
        try {
          language.img = require('./assets/flags/' + language.countryCodeLang + '.png')
        } catch {
          language.img = ''
        }
        if (language.flag && language.flag.length > 0) { language.img = language.flag }
        // check if it is the session language
        if (sessionLanguageIsInConfig) {
          if (this.sessionLanguage === composedLanguageCode) {
            this.setSelectedLanguage(language)
            localStorage.lang = this.sessionLanguage
            // Now setting the phone country code directly to be used in PhoneField instead of the countryCode.
            // Restcountries now only used if not defined on the language
            this.setDefaultCountryPrefix(language.phoneCountryCode)
            document.documentElement.setAttribute('lang', language.lang)
          }
        } else {
          if (language.default) {
            this.setSelectedLanguage(language)
            this.sessionLanguage = composedLanguageCode
            // save session language
            localStorage.lang = this.sessionLanguage
            // Now setting the phone country code directly to be used in PhoneField instead of the countryCode.
            // Restcountries now only used if not defined on the language
            this.setDefaultCountryPrefix(language.phoneCountryCode)
          }
        }
        if (language && language.default) document.documentElement.setAttribute('lang', language.lang)
        this.languages.push(language)
      }
      this.getMessages(this.selectedLanguage.lang + '_' + this.selectedLanguage.countryCodeCountry).then(response => {
        _this.setMessages(response.data)
        _this.checkRTL()
        _this.replaceAgreementLinks()
        _this.setTitle()
      })
    },
    // Initialize rtl if language translation file has set the property direction to rtl
    checkRTL () {
      if (this.funcDebug) console.log('App:method:checkRTL')
      if (this.messages['direction'] === 'rtl') {
        document.documentElement.setAttribute('dir', 'rtl')
        this.$vuetify.rtl = true
      } else {
        document.documentElement.setAttribute('dir', 'ltr')
        this.$vuetify.rtl = false
      }
    },
    focusFirstStep () {
      // Emit route name payload to set focus to first interactive element
      eventHub.$emit('focusFirstEvent', this.$route.name)
    },
    checkUrlParams () {
      if (this.funcDebug) console.log('App:method:checkUrlParams')
      if (this.$route && this.$route.params && this.$route.name !== 'search' && this.$route.name !== 'confirmation') {
        this.urlParams = this.$route.params
        this.startPage = this.$route.name
      }
    },
    replaceAgreementLinks () {
      if (this.funcDebug) console.log('App:method:replaceAgreementLinks')
      var agreementHtml1 = this.messages.contactDetailsContentAgreementText1
      if (agreementHtml1.indexOf('<link>') >= 0) {
        agreementHtml1 = agreementHtml1.replace('<link>', '<a href="#">')
        agreementHtml1 = agreementHtml1.replace('</link>', '</a>')
      }
      this.setMessagesAgreementHtml1(agreementHtml1)
      var agreementHtml2 = this.messages.contactDetailsContentAgreementText2
      if (agreementHtml2.indexOf('<link>') >= 0) {
        agreementHtml2 = agreementHtml2.replace('<link>', '<a href="#">')
        agreementHtml2 = agreementHtml2.replace('</link>', '</a>')
      }
      this.setMessagesAgreementHtml2(agreementHtml2)
    },
    setTitle () {
      if (this.funcDebug) console.log('App:method:setTitle')
      let title = this.messages.applicationHtmlTitle
      document.title = title
    },
    increaseReloadKey () {
      if (this.funcDebug) console.log('App:method:increaseReloadKey')
      this.reloadKey += 1
    },
    showErrorMessage (error) {
      if (this.funcDebug) console.log('App:methods:showErrorMessage: ' + error)
      if (error.response && error.response.status === 400) {
        if (this.messages) {
          if (this.messages.errorMessageNetworkError) this.connectionErrorMessage = this.messages.errorMessageOtherError
        }
        this.showReloadButton = false
      } else {
        if (this.messages) {
          if (this.messages.errorMessageNetworkError) this.connectionErrorMessage = this.messages.errorMessageNetworkError
          if (this.messages.reloadButtonText) this.reloadButtonText = this.messages.reloadButtonText
        }
        this.showReloadButton = true
      }
      this.mounted = true
      this.connectionError = true
    },
    reloadConfiguration (routingObj) {
      if (this.funcDebug) console.log('App:methods:reloadConfiguration')
      this.mounted = false
      this.getConfiguration().then(response => {
        if (this.funcDebug) console.log('App:methods:reloadConfiguration:getConfiguration.then')
        // Assign data to our variable that have been declared in our Vue App data object.
        let loadedConfiguration = this.convertObject(response.data)
        this.setSecurityToken(response.data.token)
        this.setConfiguration(loadedConfiguration)
        this.mounted = true
        // set the urls for css and images
        let cssFile = this.configuration.uiTenantCssUrl
        if (!this.configuration.isMultiTenant && process.env.NODE_ENV === 'development') {
          cssFile = process.env.BASE_URL + 'css/custom.css'
        }
        let file = document.createElement('link')
        file.rel = 'stylesheet'
        file.setAttribute('nonce', 'GzB2v2o2sBKmQkE5xM+0cPpjQ+rO8KyK/kI1DdxAQUY=')
        file.href = cssFile
        document.head.appendChild(file)

        let icoFileName = this.configuration.uiTenantIcoUrl
        if (!this.configuration.isMultiTenant && process.env.NODE_ENV === 'development') {
          icoFileName = process.env.BASE_URL + 'favicon.ico'
        }
        let iconFile = document.createElement('link')
        iconFile.rel = 'icon'
        iconFile.href = icoFileName
        document.head.appendChild(iconFile)
        this.imageUrl = this.configuration.uiTenantImgUrl
        if (!this.configuration.isMultiTenant && process.env.NODE_ENV === 'development') {
          this.imageUrl = process.env.BASE_URL + 'img'
        }
        document.getElementById('logo-header').style.backgroundImage = 'url("' + this.imageUrl + '/company_logo_header.png")'
        this.$router.push(routingObj)
      })
    },
    resetFindBooking () {
      if (this.funcDebug) console.log('App:methods:resetFindBooking')
      eventHub.$emit('reset-find-booking')
    }
  },
  mounted () {
    if (this.funcDebug) console.log('App:mounted')
    document.addEventListener('mousedown', (event) => {
      if (event.x !== undefined) {
        // this event was triggered, even if using the space or enter key with the keyboard. But in this case the value x
        // would be undefined, so we are only changing to mouse mode if indeed a key was clicked on with the mouse.
        // not when using the space or enter key
        document.body.classList.add('using-mouse')
        document.body.classList.remove('using-keyboard')
      }
    })

    document.addEventListener('keydown', (event) => {
      document.body.classList.add('using-keyboard')
      document.body.classList.remove('using-mouse')
    })

    // When our main Vue is loaded, we need to fetch some data that needs to be used in all components
    // Here we need to fetch Configuration from the server
    var _this = this
    this.version = Package.version
    if (Package['client-version']) {
      this.clientVersion = Package['client-version']
    }
    if (this.clientVersion) {
      console.log('Qmatic Web Booking - version ' + this.clientVersion)
    } else {
      console.log('Qmatic Web Booking - version ' + this.version)
    }
    this.mounted = false
    // load config from AdminUI
    this.getConfiguration().then(response => {
      // Assign data to our variable that have been declared in our Vue App data object.
      var loadedConfiguration = _this.convertObject(response.data)
      _this.setSecurityToken(response.data.token)
      _this.setConfiguration(loadedConfiguration)
      _this.mounted = true
      // set language
      if (_this.configuration.qmaticWebBookingLanguages) {
        if (_this.funcDebug) console.log('Language from Query: ' + _this.$route.query.lang)
        _this.checkUrlParams()
        // load from session, if present then use it
        _this.sessionLanguage = null
        if (_this.$route.query.lang) {
          // url config found, use it
          var myQueryLang = _this.$route.query.lang
          if (myQueryLang) {
            myQueryLang = myQueryLang.toLowerCase()
            _this.sessionLanguage = myQueryLang
          }
        } else if (localStorage.lang) {
          _this.sessionLanguage = localStorage.lang
        }
        _this.queryParams = _this.$route.query
        _this.initLanguage(_this.configuration.qmaticWebBookingLanguages)
      }
      if (_this.configuration.captchaEnabled) {
        let recaptchaScript = document.createElement('script')
        recaptchaScript.setAttribute('src', 'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit')
        document.head.appendChild(recaptchaScript)
      }
      // set the urls for css and images
      let cssFile = _this.configuration.uiTenantCssUrl
      if (!_this.configuration.isMultiTenant && process.env.NODE_ENV === 'development') {
        cssFile = process.env.BASE_URL + 'css/custom.css'
      }
      let file = document.createElement('link')
      file.rel = 'stylesheet'
      file.setAttribute('nonce', 'GzB2v2o2sBKmQkE5xM+0cPpjQ+rO8KyK/kI1DdxAQUY=')
      file.href = cssFile
      document.head.appendChild(file)

      let icoFileName = _this.configuration.uiTenantIcoUrl
      if (!_this.configuration.isMultiTenant && process.env.NODE_ENV === 'development') {
        icoFileName = process.env.BASE_URL + 'favicon.ico'
      }
      let iconFile = document.createElement('link')
      iconFile.rel = 'icon'
      iconFile.href = icoFileName
      document.head.appendChild(iconFile)
      _this.imageUrl = _this.configuration.uiTenantImgUrl
      if (!this.configuration.isMultiTenant && process.env.NODE_ENV === 'development') {
        _this.imageUrl = process.env.BASE_URL + 'img'
      }
      document.getElementById('logo-header').style.backgroundImage = 'url("' + _this.imageUrl + '/company_logo_header.png")'
    }).catch(error => {
      console.log('Error getting configuration:' + error)
      this.mounted = true
      this.connectionError = true
      /* this.$router.push({
        path: '500'
      }) */
    })
    this.getServiceTemplates().then(response => {
      // save the templates in the store
      _this.setServiceTemplates(response.data)
    })
    // this.$refs.firstFocus.$el.focus();
  },
  created () {
    if (this.funcDebug) console.log('App:created')
    eventHub.$on('show-warning', (textData) => this.showMessage(textData, 'warning'))
    eventHub.$on('show-error', (textData) => this.showMessage(textData, 'error'))
    eventHub.$on('show-info', (textData) => this.showMessage(textData, 'info'))
    eventHub.$on('hide-message', () => this.hideMessage())
    eventHub.$on('reload-configuration', (routingObj) => this.reloadConfiguration(routingObj))
    eventHub.$on('show-error-message', (error) => this.showErrorMessage(error))

    /* let getUrl = window.location
    let tenant = getUrl.hostname.replaceAll('.', '_')
    // Testing if the customer css file exists
    var request = new XMLHttpRequest()
    request.open('GET', cssFile, true)
    request.onreadystatechange = function () {
      if (request.readyState === 4) {
        if (request.status === 404) {
          // the customer css does not exist, so use the default one
          file.href = './css/custom.css'
          document.head.appendChild(file)
        } else {
          file.href = cssFile
          document.head.appendChild(file)
        }
      }
    }
    request.send()
    // now load the customer favicon if available
    let iconFile = document.createElement('link')
    iconFile.rel = 'icon'
    let iconFileName = this.tenantUrl + 'favicon.ico'
    var request2 = new XMLHttpRequest()
    request2.open('GET', iconFileName, true)
    request2.onreadystatechange = function () {
      if (request2.readyState === 4) {
        if (request2.status === 404) {
          // the customer icon file name does not exist, so use the default one
          iconFile.href = './favicon.ico'
          document.head.appendChild(iconFile)
        } else {
          iconFile.href = iconFileName
          document.head.appendChild(iconFile)
        }
      }
    }
    request2.send() */
  },
  updated () {
    if (this.funcDebug) console.log('App:updated')
    /* if (this.error && this.errorCode !== 500) {
      this.error = false
    } */
  },
  beforeDestroy () {
    if (this.funcDebug) console.log('App:beforeDestroy')
    eventHub.$off('show-warning')
    eventHub.$off('show-error')
    eventHub.$off('show-info')
    eventHub.$off('hide-message')
    eventHub.$off('reload-configuration')
    eventHub.$off('show-error-message')
  }
}
</script>
<style lang="scss">
@import "./assets/fonts/Roboto-material-icons.css";
@import '~material-design-icons-iconfont/dist/material-design-icons.css';

body {
  margin: 0;
  font-weight: 400;
  color: var(--default-font-color) !important;
  font-family: CustomQWBFont, Roboto, Arial, sans-serif;
  font-size: 16px;
  height: 100vh;
  padding: 0;
  & .primary--text {
    color: var(--input-label-text-color-focus) !important;
  }
  & .accent--text {
    caret-color: var(--focused-element) !important;
  }
}

i.v-icon.material-icons.theme--light.accent--text {
  color: var(--primary-btn-text-color) !important;
}

.contstep i.v-icon.material-icons.theme--light.accent--text {
    color: var(--primary-color) !important;
}

.relative {
  position: relative;
}
.screen-reader-only {
  position: absolute;
  height: 1px;
  width: 1px;
  clip: rect(1px 1px 1px 1px);
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: polygon(0px 0px, 0px 0px, 0px 0px);
  -webkit-clip-path: polygon(0px 0px, 0px 0px, 0px 0px);
  overflow: hidden !important;
}

.show-on-focus {
    position: absolute !important;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0,0,0,0);
    top:1px;
}

.show-on-focus:focus {
    margin: 18px !important;
    clip: rect(0,0,0,0);
    position: relative !important;
    width: auto !important;
    height: auto !important;
    background-color: var(--primary-btn-bg-color) !important;
    color: var(--primary-btn-text-color) !important;
    display: inline-block;
    text-align: center !important;
    top: 3px !important;
    height: 43px !important;
    z-index: 9999 !important;
    padding: 10px;
    @at-root [dir="rtl"] & {
    left: auto;
    right: 0;
    margin: 0 16px 0 0;
  }
}

.header {
  background-color: var(--header-bg-color) !important;
  border-bottom: 3px solid;
  border-color: var(--header-border-bottom-color) !important;
  -ms-flex-negative: 0;
  flex-shrink: 0;
  height: 85px;
  width: 100%;
  display: var(--display-header);
}

.v-btn {
  letter-spacing: normal !important;
}

.theme--light.v-btn-toggle{
  border-radius: 0 !important;
  button {
    border:none !important;
  }
}

.appointment-card.v-sheet {
  padding: 0;
  .v-sheet {
    padding: 10px 0 0;
    font-size: 14px;
  }
  .v-sheet + .v-sheet {
    padding: 0;
  }
}

.find-booking-btn.v-btn.v-btn--text {
  position: absolute;
  top: 20px;
  left: 0;
  height: 43px;
  font-weight: 400;
  font-size: 14px;
  margin: 0 0 0 16px;
  color: var(--primary-btn-text-color) !important;
  background-color: var(--primary-btn-bg-color) !important;

  @at-root [dir="rtl"] & {
    left: auto;
    right: 0;
    margin: 0 16px 0 0;
  }
}

.reset-btn.v-btn--active:before, .v-btn:hover:before, .v-btn:focus:before{
  background-color: initial !important;
}

.header a:link {
  border-bottom: none;
}

.header a:hover {
  color: var(--focused-element) !important;
}

.brand {
  display: block;
  height: 54px;
  margin: 0 auto;
  width: 290px;
  position: absolute;
  top: 13px;
  left: 0;
  right: 0;
  background-repeat: none;
  background-position: center;
}

#vueapp .container.main-container {
  max-width: 900px;
  display: block;
  margin: 0 auto;
  height: 100%;
}

#vueapp .main-container-error {
  background-color: #1a1c34;
  width: 100%;
  color: #FFFFFF;
  height: 100%;
  max-width: 100% !important;
  text-align: center;
  font-size: 35px;
  padding: 100px 30% 200px 30% !important;
}

#vueapp .header-error {
  background-color: #1a1c34;
  width: 100%;
  color: #FFFFFF;
  max-width: 100% !important;
}

#vueapp .footer {
  background: transparent;
}
.footer .version {
  padding-right: 16px;
}

#vueapp.theme--light.v-application {
  background: linear-gradient(180deg, var(--main-bg-gradient-top-color) 0%, var(--main-bg-gradient-bottom-color) 100%) !important;
}

.input-btn.v-btn {
  background-color: var(--primary-btn-bg-color) !important;
  color: var(--primary-btn-text-color) !important;
  border-radius: 0;
}

.wrapper {
  /*border: 2px solid;
  border-color: var(--primary-border-color) !important;*/
  background-color: var(--wrapper-bg-color-primary) !important;
}

h1 {
  text-align: center;
  padding-top: 24px;
  color: var(--default-font-color);
}

h2 {
  font-weight: 400;
  color: var(--primary-bg-text-color);
}

.progress-bar{
  color: var(--progress-bar-color);
}

.info-text {
  font-size: 18px;
  font-weight: 300;
  padding: 16px 24px !important;
  margin-bottom:5px;
  color: var(--default-font-color) !important;

  &.agreement {
    margin: 0;
    padding: 16px 24px;
  }

  @at-root [dir="rtl"] & {
    text-align: right;
  }
}

.v-date-picker-header__value > div{
  margin:5px 0;
}

.v-btn.v-btn--text.theme--light:hover,
.v-btn.v-btn--icon.theme--light:hover,
.phone-verification-btn.v-btn.v-btn--text.theme--light:hover,
.submit.v-btn.v-btn--text.theme--light:hover,
.submit.back.v-btn.v-btn--text.theme--light:hover,
.cancel.v-btn.v-btn--text.theme--light:hover,
.edit.v-btn.v-btn--text.theme--light:hover,
.reschedule .submit.v-btn.v-btn--text.theme--light:hover,
.v-btn.v-btn--absolute.v-btn--icon.v-btn--right.theme--light.error:hover,
.reset-btn.v-btn.v-btn--icon.theme--light.error:hover {
  background-color: var(--defined-focused-element) !important;
  color: var(--primary-btn-text-color) !important;
}

.submit.v-btn.v-btn--text.theme--light {
  height: 64px;
  background-color: var(--primary-btn-bg-color) !important;
  color: var(--primary-btn-text-color) !important;
  font-weight: 400;
  font-size: 17px;
  min-width: 300px;
  border-radius: 0;
  margin: 0 24px 16px;
}

.submit.v-btn.v-btn--disabled.v-btn--text.theme--light,
.phone-verification-btn.v-btn.v-btn--disabled.v-btn--text.theme--light,
.send-verification-btn.v-btn.v-btn--disabled.v-btn--text.theme--light {
  background-color: var(--primary-btn-bg-disabled-color) !important;
}

.reschedule.content {
  padding: 0 0 16px;
}

.phoneErrorMessage {
    color: #ff5252 !important;
    margin: 8px 0 8px 12px;
  }

.v-list-item--dense .v-list-item__content, .v-list--dense .v-list-item .v-list-item__content {
  padding: 0 !important;
}

.v-sheet.v-card {
  border-radius: 0px;
}

.v-sheet.v-card:not(.v-sheet--outlined){
  box-shadow: none;
}

.confirmation-text {
  padding-bottom: 16px;
}

.info-text.appointment-details {
  padding: 24px;
}

.v-select.v-select--chips .v-select__selections {
  padding-top: 18px;
}

.custom-field .v-input--has-state.error--text .v-label {
  top: 28px !important;
}

.custom-field .v-input__append-inner {
  margin-top: 16px !important;
}

.content .wrapper h1 {
  padding: 20px;
}

.primary-color {
  background-color: var(--primary-color) !important;
  color: var(--primary-bg-text-color) !important;
}

.submit.back.v-btn.v-btn--text.theme--light {
  display: block;
  margin: 36px 0 0 !important;
}

a:link {
  border-bottom: 1px solid #9199aa;
  text-decoration: none;
}

.v-application {
  a,
  a:active,
  a:link,
  a:visited {
    color: #2a5bb4;
    outline: none;
  }
  font-family: CustomQWBFont, Roboto, Arial, sans-serif;
}

a:focus {
  outline-color: blue !important;
  outline: solid;
}

a:hover {
  color: var(--focused-element) !important;
}

.v-input--radio-group.radio-group-focused:not(.selected), .service-chkbox.v-input--is-focused{
    background-color: #e5e5e5 !important;
  }

.v-text-field--outline.v-input--is-focused
  .v-input__slot
  .selected-language-name {
  text-decoration: underline;
}

.v-text-field.v-input--has-state > .v-input__control > .v-input__slot:before {
  border: none;
}

.v-text-field > .v-input__control > .v-input__slot:after {
  border:none;
}

.v-textarea textarea {
  // stops text display over label
  margin-top: 20px;
}

.using-keyboard{
  div:not(#main-container):focus, button:not(#dobPickerIcon):focus, a:focus, li:focus, h1:focus, .appointment-details:focus, .confirmation-row:focus,
    input:not(#customFields1):not(#customFields2):not(#customFields3):not(#customFields4):not(#customFields5):not(#customFields6):not(#customFields7):not(#customFields8):not(#customFields9):not(#customFields10):not(#verification-code):not(#LastName):not(#FirstName):not(#Email):not(#Notes):not(#countryCode):not(#Phone):not(#DOB):not(#ConfirmPhone):not(#ConfirmEmail):not(#AppRef):not(#CustRef):not(#BookingId):not(#language-selection):focus  {
    outline: var(--tabfocus-outline-width) var(--tabfocus-outline-style) var(--tabfocus-outline-color) !important;
    opacity:1 !important;
    box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
    //box-shadow: var(--tabfocus-shadow) !important;
  }
  .v-input--is-focused.focused-border > .v-input__control > .v-input__slot {
    border: var(--tabfocus-outline-color) !important;
    outline: var(--tabfocus-outline-width) var(--tabfocus-outline-style) var(--tabfocus-outline-color) !important;
    opacity:1 !important;
    box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
  }
  /*.service-chkbox.v-input--is-focused {
    border: var(--tabfocus-outline-color) !important;
    outline: var(--tabfocus-outline-width) var(--tabfocus-outline-style) var(--tabfocus-outline-color) !important;
    opacity:1 !important;
    box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
    -webkit-box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
    -moz-box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
  }*/

  // this is the border for the checkbox for the confirmation checkbox for privacy policy
  .info-text.v-input--is-focused > .v-input__control > .v-input__slot input {
    border: var(--tabfocus-outline-color) !important;
    outline: var(--tabfocus-outline-width) var(--tabfocus-outline-style) var(--tabfocus-outline-color) !important;
    opacity:1 !important;
    box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
    -webkit-box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
    -moz-box-shadow: var(--tabfocus-shadow-width) var(--tabfocus-shadow-color) !important;
  }

}
.info-text.agreement .v-input__slot{
    align-items: normal;
  }

.v-text-field--outline.v-input--is-focused .v-input__slot {
  border: 2px solid;
}

.v-dialog>.v-card>.v-card__text{
  padding: 16px;
}

.theme--light.v-list .v-list__tile--highlighted {
  background-color: var(--focused-element) !important;
  color: var(--primary-bg-text-color) !important;
}

.theme--light.v-list .v-list__tile--highlighted .language-option {
  color: var(--primary-bg-text-color) !important;
}

.dialog-title {
  background-color: var(--primary-color) !important;
  color: var(--primary-bg-text-color) !important;
}

.edit.v-btn.v-btn--text.theme--light {
  background-color: var(--primary-btn-bg-color) !important;
}

.cancel.v-btn.v-btn--text.theme--light {
  background-color: var(--primary-btn-bg-color) !important;
}

.cancel.v-btn.v-btn--text.theme--light,
.edit.v-btn.v-btn--text.theme--light {
  color: var(--primary-btn-text-color) !important;
  font-weight: 400;
  height: 56px;
}

.grecaptcha-badge {
  position: fixed;
  bottom: 0;
  right: 0;
}

.v-text-field.v-text-field--solo .v-input__control {
  min-height: 56px;
}
.input-field.v-text-field--outline.v-input--is-disabled .v-input__slot {
  border-color: #d9d9d9;
  background: #616161;
}

.confirmation-service-adult{
  display: inline-block;
}

.v-input--selection-controls {
    margin:8px 0;
  }

/*NEW*/
.container{
  padding:24px;
}

.booking-container{
  padding: 16px;
  background-color: var(--wrapper-bg-color-primary);
  .v-expansion-panel:before{
      box-shadow: none;
      -webkit-box-shadow: none;
  }
}

.v-expansion-panel.selected label {
  color: #fff;
}

.selected {
  background-color: var(--focused-element) !important;
  &.v-input label {
    color: #fff !important;
  }
  .v-label{
    color: #fff !important;
  }
}

@media screen and (max-width: 600px) {
  h1 {
    font-size: 27px;
    padding: 16px;
  }

  .container{
    padding:0px;
  }

  .booking-container{
    padding:0px !important;
  }

  .custom-field {
    .v-input__slot {
      min-height: auto !important;
      display: flex !important;
      align-items: center !important;
    }
  }
  .v-text-field--box .v-input__slot, .v-text-field--outline .v-input__slot {
  min-height: auto!important;
  display: flex!important;
  align-items: center!important;
  }
  .find-booking-btn.v-btn.v-btn--text {
    min-width: 70px;
  }
  .find-booking-btn .find-booking-text {
    display: none;
  }

  h2 {
    font-size: 25px;
    font-weight: 400;
  }

  .brand {
    max-height: 50px;
    width: 100px;
    background-size: contain;
    -webkit-background-size: contain;
  }

  .brand img {
    height: 44px;
    width: 100px;
    display: block;
    margin: auto;
  }

  .header {
    height: 71px;
  }

  #vueapp {
    padding-top: 0;
  }
  .submit.v-btn.v-btn--text.theme--light,
  .submit.back.v-btn.v-btn--text.theme--light {
    width: auto;
    font-size: 16px;
    margin: 16px;
  }

  .submit.back.v-btn.v-btn--text.theme--light {
    width: 100%;
  }

  .submit.v-btn.v-btn--text.theme--light .v-btn__content {
    white-space: normal;
  }

  .send-verification-btn.v-btn{
    width:48%;
    padding: 0 8px;
    overflow: hidden;
  }

  .send-verification-btn.v-btn .v-btn__content{
    white-space: normal;
    line-height: 18px;
  }

  .info-text {
    font-size: 18px;
    font-weight: 300;
    padding: 16px !important;
  }

  .content .wrapper h1 {
    padding: 16px 0;
  }

  .submit.v-btn.v-btn--text.theme--light {
    margin: 0 16px 16px;
  }
  #vueapp .main-container-error {
    padding: 100px 50px 50px 50px !important;
  }

}

@media only screen and (max-width: 359px) and (min-width: 300px) {
    h1 {
      font-size: 24px;
    }

    .submit.v-btn.v-btn--text.theme--light {
      min-width: 250px;
    }
    #vueapp .main-container-error {
      padding: 70px 30px 30px 40px !important;
    }
  }

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  /* IE10+ CSS styles go here */
  * {
    -o-transition-property: none !important;
    -moz-transition-property: none !important;
    -ms-transition-property: none !important;
    -webkit-transition-property: none !important;
    transition-property: none !important;
    -o-transform: none !important;
    -moz-transform: none !important;
    -ms-transform: none !important;
    -webkit-transform: none !important;
    -webkit-transform-origin: none !important;
    transform-origin: none !important;
    transform: none !important;
    -webkit-animation: none !important;
    -moz-animation: none !important;
    -o-animation: none !important;
    -ms-animation: none !important;
    animation: none !important;
  }

  .v-input.input-field.v-text-field.v-input--is-focused label,
  .v-input.v-text-field--box.v-text-field--enclosed.v-input--is-focused label,
  .v-input.v-text-field--box.v-text-field--enclosed.v-input--is-disabled label,
  .v-input.v-text-field--enclosed.v-text-field--outline.v-input--is-disabled
    label,
  .v-input.v-text-field--enclosed.v-text-field--outline.v-input--is-focused
    label,
  .v-input.input-field.v-text-field.v-input--is-label-active label {
    font-size: 12px;
    top: 0;
  }
  #vueapp .application--wrap {
    display: block;
  }
  #vueapp .footer {
    width: 100%;
    position: absolute;
    bottom: -80px;
  }
}

.message-error-style, .message-warning-style, .message-info-style {
  z-index: 999;
  font-size: 18px;
  font-weight: 300;
}

.v-snack__wrapper.theme--dark.message-error-style {
  background-color: #ff5252 !important;
}

.v-snack__wrapper.theme--dark.message-warning-style {
  background-color: var(--message-warning-bg-color) !important;
}

.v-snack__wrapper.theme--dark.message-info-style {
  background-color: var(--message-info-bg-color) !important;
}

.wrapper .info, .v-application .v-alert.info {
  border-radius: 0;
  font-size: 14px;
  background-color: var(--message-info-bg-color) !important;
}

.container .warning{
  background-color: var(--message-warning-bg-color) !important;
}

// border text field
.v-input.v-text-field > .v-input__control > .v-input__slot {
  border: 1px solid var(--input-border-color) !important;
  background: white !important;
  border-radius: 0;
  padding: 0 10px;
}

.v-input.v-text-field > .v-input__control > .v-input__slot input {
  margin-top: 22px;
}

// apply custom font
.application {
  font-family: 'CustomQWBFont', 'Roboto', sans-serif;
}

input {
  font-family: 'Roboto', sans-serif;
}

// add some space on regions expansion panels
.region-wrapper {
  .v-expansion-panel {
    margin: 0;
    padding: 4px 0;
  }
  .v-expansion-panel:not(:last-of-type){
    border-bottom:1px solid rgba(0,0,0,.12);
  }
  .v-expansion-panel::after{
    border:none;
  }
}

.region-wrapper {
  box-shadow: 0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12);
  -webkit-box-shadow: 0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12);
}

.v-snack--multi-line .v-snack__content {
  height: auto !important;
}

.tempStyle {
  margin-left: 300px !important;
}

.selectedColor{
  color:var(--primary-btn-text-color) !important;
}

.v-expansion-panel-content__wrap {
  padding: 0;
}

.v-expansion-panels{
  border-radius: 0px;
}

.v-expansion-panel {
    margin: 4px 0;
    .v-card {
      margin:6px 0 0;
    }
  }

/*.v-expansion-panel .v-expansion-panel {
  margin-top: 4px !important;
}*/

small {
  line-height: 1.5em;
  font-size: 0.825rem;
}

.v-expansion-panel.service-expansion-panel {
  label {
    font-size: 16px;
    line-height: 24px;
  }
  .v-card {
    background-color: var(--input-selection-bg-color);
  }
}

.v-expansion-panel--active > .v-expansion-panel-header{
  min-height: auto;
}

.v-expansion-panel-header{
  padding: 12px 24px;
  .container {
    padding: 0;
  }
}

.theme--light.v-expansion-panels .v-expansion-panel{
  border-radius: 0 !important;
}

#skiplink .v-btn__content {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: normal !important;
}
</style>
